import { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { PageHeader } from '@components/PageHeader';
import { Card } from '@components/ui/Card';
import { Button } from '@components/ui/Button';
import { Tabs } from '@components/ui/Tabs';
import { EstateMap, type EstateMapMode } from '@components/EstateMap';
import { StageManager } from '@components/StageManager';
import { useEstateStore } from '../../stores/estateStore';
import { processNswEstate, isNswPlan } from '@services/nswCadastreApi';
import { processVicEstate } from '@services/vicCadastreApi';
import type { AustralianState, Classification, EstateJsonData } from '@types';
import { jsonDataToItems, jsonDataToMetadata } from '../../utils/mergeEstates';
import { validateStageConfiguration } from '../../utils/stages';

const CLASSIFICATION_OPTIONS: { value: Classification; label: string; color: string }[] = [
  { value: 'lot', label: 'Lot', color: '#2CBB65' },
  { value: 'road', label: 'Road', color: '#2E2E2E' },
  { value: 'reserve', label: 'Reserve', color: '#27A675' },
  { value: 'water', label: 'Water', color: '#21B0F3' },
  { value: 'future', label: 'Future', color: '#9E9E9E' },
];

const LOT_STATUS_OPTIONS = [
  { value: 'available', label: 'Available', color: '#2CBB65' },
  { value: 'hold', label: 'Hold', color: '#2196F3' },
  { value: 'under-offer', label: 'Under Offer', color: '#FF6F00' },
  { value: 'sold', label: 'Sold', color: '#d60014' },
];

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

export const ReviewEdit = () => {
  const navigate = useNavigate();
  const { id: planNumber } = useParams<{ id: string }>();
  const location = useLocation();
  const locationState = location.state as {
    lotNumber?: string;
    address?: string;
    preloaded?: boolean;
    state?: AustralianState;
  } | null;
  const nswLotNumber = locationState?.lotNumber ?? '1';
  const estateAddress = locationState?.address ?? '';
  const isPreloaded = locationState?.preloaded === true;
  const selectedState =
    locationState?.state ?? (planNumber && isNswPlan(planNumber) ? 'NSW' : 'VIC');

  const {
    metadata,
    items,
    selectedCode,
    jobStatus,
    jobMessage,
    jobError,
    loadEstateWithItems,
    classify,
    updateLotStatus,
    updateLotPrice,
    bulkAdjustPrice,
    importPricing,
    updateStages,
    setSelected,
    translateEstate,
    reset,
    setJobState,
    clearJob,
    getLots,
    getReferences,
    getDeletedCount,
    getSelected,
  } = useEstateStore();

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const jobVersionRef = useRef(0);

  // Status dropdown state
  const [statusDropdownCode, setStatusDropdownCode] = useState<string | null>(null);

  // Pricing tab state
  const [editingPriceCode, setEditingPriceCode] = useState<string | null>(null);
  const [editingPriceValue, setEditingPriceValue] = useState('');
  const [bulkAmount, setBulkAmount] = useState('');
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mapMode, setMapMode] = useState<EstateMapMode>('aerial');
  const [alignmentMode, setAlignmentMode] = useState(false);

  const stopProcessingTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const invalidateActiveJob = useCallback(() => {
    jobVersionRef.current++;
  }, []);

  const loadEstateFromData = useCallback(
    (data: EstateJsonData) => {
      const importedMetadata = jsonDataToMetadata(data);
      loadEstateWithItems(
        {
          ...importedMetadata,
          planNumber: planNumber || '',
          name: data.name || planNumber || 'Unknown Estate',
        },
        jsonDataToItems(data)
      );
    },
    [loadEstateWithItems, planNumber]
  );

  const startJob = useCallback(
    async (version: number) => {
      if (!planNumber) return;
      setElapsedSeconds(0);

      if (selectedState !== 'VIC' && selectedState !== 'NSW') {
        setJobState({
          jobStatus: 'failed',
          jobMessage: `${selectedState} lookup is unavailable`,
          jobError:
            'Direct cadastral plan loading currently supports Victoria and New South Wales.',
          jobId: null,
        });
        return;
      }

      const isNsw = selectedState === 'NSW' || isNswPlan(planNumber);
      const stateLabel = isNsw ? 'NSW' : 'VIC';
      setJobState({
        jobStatus: 'submitting',
        jobMessage: isNsw
          ? `Locating parent lot (Lot ${nswLotNumber} ${planNumber})...`
          : `Querying Vicmap for ${planNumber}...`,
        jobError: null,
        jobId: null,
      });

      stopProcessingTimer();
      timerRef.current = setInterval(() => {
        setElapsedSeconds((seconds) => seconds + 1);
      }, 1000);

      try {
        let data: EstateJsonData;
        if (isNsw) {
          // ── NSW: client-side processing via NSW SIX Cadastre API ──────────────
          data = await processNswEstate(
            nswLotNumber,
            planNumber,
            estateAddress || undefined,
            (msg) => {
              if (version !== jobVersionRef.current) return;
              setJobState({ jobStatus: 'running', jobMessage: msg });
            }
          );
        } else {
          // ── VIC: client-side processing via the public Vicmap Parcel API ─────
          data = await processVicEstate(planNumber, estateAddress || undefined, (msg) => {
            if (version !== jobVersionRef.current) return;
            setJobState({ jobStatus: 'running', jobMessage: msg });
          });
        }

        if (version !== jobVersionRef.current) return;
        stopProcessingTimer();
        setJobState({ jobStatus: 'downloading', jobMessage: 'Building estate data...' });
        loadEstateFromData(data);
      } catch (err) {
        if (version !== jobVersionRef.current) return;
        stopProcessingTimer();
        setJobState({
          jobStatus: 'failed',
          jobMessage: `${stateLabel} lookup failed`,
          jobError: err instanceof Error ? err.message : `Failed to process ${stateLabel} estate`,
        });
      }
    },
    [
      planNumber,
      selectedState,
      nswLotNumber,
      estateAddress,
      setJobState,
      stopProcessingTimer,
      loadEstateFromData,
    ]
  );

  // Start job on mount if no data loaded
  useEffect(() => {
    if (!planNumber) return;

    // If data was pre-loaded (e.g. from merge), skip job submission entirely
    if (isPreloaded && jobStatus === 'completed' && items.length > 0) {
      return;
    }

    // If store already has data for this plan number, skip
    if (metadata && metadata.planNumber === planNumber && jobStatus === 'completed') {
      return;
    }

    // Clear any previous state and start fresh
    const version = ++jobVersionRef.current;
    clearJob();
    startJob(version);

    return () => {
      invalidateActiveJob();
      stopProcessingTimer();
    };
  }, [planNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleItemClick = useCallback(
    (code: string) => {
      setSelected(selectedCode === code ? null : code);
    },
    [selectedCode, setSelected]
  );

  const handleEstateTranslate = useCallback(
    (delta: { lat: number; lng: number }) => translateEstate(delta),
    [translateEstate]
  );

  const nudgeEstate = useCallback(
    (lat: number, lng: number) => translateEstate({ lat, lng }),
    [translateEstate]
  );

  const selected = getSelected();
  const lots = getLots();
  const references = getReferences();
  const deletedCount = getDeletedCount();
  const stageValidation = validateStageConfiguration(metadata?.stages ?? [], lots);

  // Loading / Progress UI
  if (jobStatus !== 'completed') {
    return (
      <div>
        <PageHeader
          title={planNumber || 'Loading Estate'}
          subtitle={
            selectedState === 'NSW' || (planNumber && isNswPlan(planNumber))
              ? 'Querying NSW SIX Cadastre API'
              : 'Querying Vicmap Parcel API'
          }
          showBackButton
        />

        <div className="mx-auto max-w-lg">
          <Card>
            {jobStatus === 'failed' ? (
              // Error state
              <div className="py-8 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                  <svg
                    className="h-8 w-8 text-red-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-bold text-gray-800">{jobMessage}</h3>
                <p className="mb-6 text-sm text-red-600">{jobError}</p>
                <div className="flex justify-center gap-3">
                  <Button variant="outlined" onClick={() => navigate('/find')}>
                    Go Back
                  </Button>
                  <Button onClick={() => startJob(++jobVersionRef.current)}>Retry</Button>
                </div>
              </div>
            ) : (
              // Progress state
              <div className="py-8 text-center">
                <div className="mx-auto mb-6">
                  <svg
                    className="mx-auto h-12 w-12 animate-spin text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-bold text-gray-800">{jobMessage}</h3>
                {elapsedSeconds > 0 && (
                  <p className="mb-2 text-sm text-gray-500">
                    Elapsed: {formatElapsed(elapsedSeconds)}
                  </p>
                )}
                <p className="mb-4 text-xs text-gray-400">
                  {selectedState === 'NSW' || (planNumber && isNswPlan(planNumber))
                    ? 'This typically takes 5-30 seconds.'
                    : 'This typically takes a few seconds.'}
                </p>

                {/* Progress steps */}
                <div className="mx-auto mt-6 max-w-xs text-left">
                  {(['submitting', 'running', 'downloading'] as const).map((step, i) => {
                    const labels =
                      selectedState === 'NSW' || (planNumber && isNswPlan(planNumber))
                        ? ['Locating parent lot', 'Fetching lots', 'Building estate']
                        : ['Querying Vicmap', 'Processing parcels', 'Building estate'];
                    const stepOrder = {
                      submitting: 0,
                      queued: 0,
                      running: 1,
                      downloading: 2,
                      idle: -1,
                      completed: 3,
                      failed: -1,
                    };
                    const current = stepOrder[jobStatus] ?? -1;
                    const isActive = current === i;
                    const isDone = current > i;

                    return (
                      <div key={step} className="flex items-center gap-3 py-1.5">
                        <div
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                            isDone
                              ? 'bg-green-500 text-white'
                              : isActive
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-500'
                          }`}
                        >
                          {isDone ? '\u2713' : i + 1}
                        </div>
                        <span
                          className={`text-sm ${
                            isActive
                              ? 'font-bold text-gray-800'
                              : isDone
                                ? 'text-gray-500'
                                : 'text-gray-400'
                          }`}
                        >
                          {labels[i]}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6">
                  <Button
                    variant="text"
                    onClick={() => {
                      invalidateActiveJob();
                      stopProcessingTimer();
                      navigate('/find');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  }

  // Estate loaded — show map + classification UI
  if (!metadata) return null;

  return (
    <div>
      <PageHeader
        title={metadata.name}
        subtitle={`${metadata.stage || 'No stages'} • ${lots.length} lots • ${references.length} references${deletedCount > 0 ? ` • ${deletedCount} deleted` : ''}`}
        showBackButton
        actions={
          <>
            <Button variant="outlined" onClick={reset}>
              Reset
            </Button>
            <Button
              onClick={() => navigate(`/export/${planNumber}`)}
              disabled={stageValidation.errors.length > 0}
              title={
                stageValidation.errors.length > 0
                  ? 'Fix the stage configuration before exporting'
                  : undefined
              }
            >
              Continue to Export
            </Button>
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-xl font-bold text-gray-800">Estate Preview</h3>
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5">
                {(['aerial', 'sales'] as EstateMapMode[]).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setMapMode(option)}
                    className={`rounded-md px-3 py-1.5 text-xs font-bold capitalize transition-colors ${
                      mapMode === option
                        ? 'bg-gray-800 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {option === 'aerial' ? 'Satellite' : 'Sales plan'}
                  </button>
                ))}
              </div>
              <Button
                size="sm"
                variant={alignmentMode ? 'primary' : 'outlined'}
                onClick={() => setAlignmentMode((enabled) => !enabled)}
              >
                {alignmentMode ? 'Done aligning' : 'Align estate'}
              </Button>
            </div>
          </div>
          {alignmentMode && (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
              <p className="text-xs text-amber-900">
                Drag the gold handle to move every estate polygon together. Use the arrows for fine
                adjustment.
              </p>
              <div className="grid grid-cols-3 gap-1" aria-label="Fine position controls">
                <span />
                <button
                  type="button"
                  title="Nudge north"
                  aria-label="Nudge north"
                  onClick={() => nudgeEstate(0.000002, 0)}
                  className="rounded border border-amber-400 bg-white px-2 py-1 text-xs hover:bg-amber-100"
                >
                  ↑
                </button>
                <span />
                <button
                  type="button"
                  title="Nudge west"
                  aria-label="Nudge west"
                  onClick={() => nudgeEstate(0, -0.000002)}
                  className="rounded border border-amber-400 bg-white px-2 py-1 text-xs hover:bg-amber-100"
                >
                  ←
                </button>
                <button
                  type="button"
                  title="Nudge south"
                  aria-label="Nudge south"
                  onClick={() => nudgeEstate(-0.000002, 0)}
                  className="rounded border border-amber-400 bg-white px-2 py-1 text-xs hover:bg-amber-100"
                >
                  ↓
                </button>
                <button
                  type="button"
                  title="Nudge east"
                  aria-label="Nudge east"
                  onClick={() => nudgeEstate(0, 0.000002)}
                  className="rounded border border-amber-400 bg-white px-2 py-1 text-xs hover:bg-amber-100"
                >
                  →
                </button>
              </div>
            </div>
          )}
          <EstateMap
            items={items}
            selectedCode={selectedCode}
            onItemClick={handleItemClick}
            center={metadata.geolocation}
            className="h-[500px] rounded-lg"
            mode={mapMode}
            alignmentMode={alignmentMode}
            onEstateTranslate={handleEstateTranslate}
          />
          <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-gray-600">
            <span className="flex items-center gap-1">
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ backgroundColor: '#2CBB65' }}
              />
              Available
            </span>
            <span className="flex items-center gap-1">
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ backgroundColor: '#2196F3' }}
              />
              Hold
            </span>
            <span className="flex items-center gap-1">
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ backgroundColor: '#FF6F00' }}
              />
              Under Offer
            </span>
            <span className="flex items-center gap-1">
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ backgroundColor: '#d60014' }}
              />
              Sold
            </span>
            <span className="flex items-center gap-1">
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ backgroundColor: '#2E2E2E' }}
              />
              Road
            </span>
            <span className="flex items-center gap-1">
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ backgroundColor: '#27A675' }}
              />
              Reserve
            </span>
            <span className="flex items-center gap-1">
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ backgroundColor: '#21B0F3' }}
              />
              Water
            </span>
          </div>
          <p className="mt-2 text-xs text-gray-500">Click a polygon to select and classify it</p>
        </Card>

        <div className="space-y-6">
          {/* Selected Item Panel */}
          {selected && (
            <Card>
              <div className="flex items-start justify-between">
                <h3 className="text-xl font-bold text-gray-800">
                  {selected.classification === 'lot' ? `Lot ${selected.code}` : selected.code}
                </h3>
                <button
                  onClick={() => setSelected(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  &times;
                </button>
              </div>

              <div className="mt-3 space-y-2">
                <p className="text-s text-gray-600">
                  {selected.landsize}m² • {selected.frontage}m frontage
                </p>

                <div>
                  <p className="mb-2 text-xs font-bold uppercase text-gray-700">Classify as:</p>
                  <div className="flex flex-wrap gap-2">
                    {CLASSIFICATION_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => classify(selected.code, opt.value)}
                        className={`rounded-full border-2 px-3 py-1 text-xs font-bold transition-colors ${
                          selected.classification === opt.value
                            ? 'border-transparent text-white'
                            : 'border-gray-300 text-gray-700 hover:border-gray-400'
                        }`}
                        style={
                          selected.classification === opt.value
                            ? { backgroundColor: opt.color }
                            : undefined
                        }
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-3 border-t border-gray-200 pt-3">
                  <Button
                    size="sm"
                    variant="text"
                    onClick={() => {
                      classify(selected.code, 'deleted');
                      setSelected(null);
                    }}
                    className="text-red-600 hover:text-red-800"
                  >
                    Delete this item
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Tabs */}
          <Card>
            <Tabs
              tabs={[
                {
                  label: `Lots (${lots.length})`,
                  content: (
                    <div className="max-h-96 space-y-2 overflow-y-auto">
                      {lots.length === 0 ? (
                        <p className="text-s text-gray-500">No lots</p>
                      ) : (
                        lots
                          .sort((a, b) => parseInt(a.code) - parseInt(b.code))
                          .map((lot) => {
                            const statusColor =
                              lot.status === 'available'
                                ? '#2CBB65'
                                : lot.status === 'sold'
                                  ? '#d60014'
                                  : lot.status === 'hold'
                                    ? '#2196F3'
                                    : '#FF6F00';
                            const isDropdownOpen = statusDropdownCode === lot.code;

                            return (
                              <div
                                key={lot.handleid}
                                onClick={() => handleItemClick(lot.code)}
                                className={`flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors hover:bg-gray-50 ${
                                  lot.code === selectedCode
                                    ? 'border-yellow-400 bg-yellow-50'
                                    : 'border-gray-200'
                                }`}
                              >
                                <div>
                                  <p className="text-s font-bold text-gray-800">Lot {lot.code}</p>
                                  <p className="text-xs text-gray-500">
                                    {lot.landsize}m² • {lot.frontage}m frontage
                                  </p>
                                </div>
                                <div className="relative">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setStatusDropdownCode(isDropdownOpen ? null : lot.code);
                                    }}
                                    className="rounded-full px-2 py-0.5 text-xs font-bold text-white transition-opacity hover:opacity-80"
                                    style={{ backgroundColor: statusColor }}
                                  >
                                    {lot.status}
                                  </button>
                                  {isDropdownOpen && (
                                    <>
                                      <div
                                        className="fixed inset-0 z-10"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setStatusDropdownCode(null);
                                        }}
                                      />
                                      <div className="absolute right-0 top-full z-20 mt-1 w-36 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                                        {LOT_STATUS_OPTIONS.map((opt) => (
                                          <button
                                            key={opt.value}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              updateLotStatus(lot.code, opt.value);
                                              setStatusDropdownCode(null);
                                            }}
                                            className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-gray-50 ${
                                              lot.status === opt.value ? 'font-bold' : ''
                                            }`}
                                          >
                                            <span
                                              className="inline-block h-2.5 w-2.5 rounded-full"
                                              style={{ backgroundColor: opt.color }}
                                            />
                                            {opt.label}
                                          </button>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })
                      )}
                    </div>
                  ),
                },
                {
                  label: `Stages (${metadata.stages?.length ?? 0})`,
                  content: (
                    <StageManager
                      lots={lots}
                      stages={metadata.stages ?? []}
                      onChange={updateStages}
                    />
                  ),
                },
                {
                  label: `References (${references.length})`,
                  content: (
                    <div className="max-h-96 space-y-2 overflow-y-auto">
                      {references.length === 0 ? (
                        <p className="text-s text-gray-500">
                          No references yet. Click a polygon on the map and classify it as a
                          reference type.
                        </p>
                      ) : (
                        references.map((ref) => (
                          <div
                            key={ref.handleid}
                            onClick={() => handleItemClick(ref.code)}
                            className={`flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors hover:bg-gray-50 ${
                              ref.code === selectedCode
                                ? 'border-yellow-400 bg-yellow-50'
                                : 'border-gray-200'
                            }`}
                          >
                            <div>
                              <p className="text-s font-bold text-gray-800">{ref.code}</p>
                              <p className="text-xs text-gray-500">
                                {ref.landsize}m² • {ref.frontage}m frontage
                              </p>
                            </div>
                            <span
                              className="rounded-full px-2 py-0.5 text-xs font-bold capitalize text-white"
                              style={{
                                backgroundColor:
                                  ref.classification === 'road'
                                    ? '#2E2E2E'
                                    : ref.classification === 'reserve'
                                      ? '#27A675'
                                      : ref.classification === 'water'
                                        ? '#21B0F3'
                                        : '#9E9E9E',
                              }}
                            >
                              {ref.classification}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  ),
                },
                {
                  label: 'Pricing',
                  content: (
                    <div className="space-y-4">
                      {/* Bulk pricing controls */}
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <p className="mb-3 text-xs font-bold uppercase text-gray-700">
                          Bulk Update
                        </p>
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <label className="mb-1 block text-xs text-gray-600">
                              Adjust all lots by ($)
                            </label>
                            <input
                              type="number"
                              value={bulkAmount}
                              onChange={(e) => setBulkAmount(e.target.value)}
                              placeholder="e.g. 10000 or -5000"
                              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          <button
                            onClick={() => {
                              const amount = parseFloat(bulkAmount);
                              if (!isNaN(amount) && amount !== 0) {
                                bulkAdjustPrice(amount);
                                setBulkAmount('');
                              }
                            }}
                            disabled={
                              !bulkAmount ||
                              isNaN(parseFloat(bulkAmount)) ||
                              parseFloat(bulkAmount) === 0
                            }
                            className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-bold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Apply
                          </button>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-bold text-gray-700 transition-colors hover:bg-gray-50"
                          >
                            Import BlocPlus JSON
                          </button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".json"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const reader = new FileReader();
                              reader.onload = (ev) => {
                                try {
                                  const data = JSON.parse(ev.target?.result as string);
                                  const importedLots = (data.lots || []).map(
                                    (l: { code: string; price: number }) => ({
                                      code: l.code,
                                      price: l.price,
                                    })
                                  );
                                  if (importedLots.length === 0) {
                                    setImportMessage('No lots found in imported file.');
                                    return;
                                  }
                                  const matched = importPricing(importedLots);
                                  setImportMessage(
                                    `Updated pricing for ${matched} of ${importedLots.length} lots.`
                                  );
                                } catch {
                                  setImportMessage('Failed to parse JSON file.');
                                }
                              };
                              reader.readAsText(file);
                              e.target.value = '';
                            }}
                          />
                          {importMessage && (
                            <span className="text-xs text-gray-600">{importMessage}</span>
                          )}
                        </div>
                      </div>

                      {/* Lot pricing list */}
                      <div className="max-h-72 space-y-2 overflow-y-auto">
                        {lots.length === 0 ? (
                          <p className="text-s text-gray-500">No lots</p>
                        ) : (
                          lots
                            .sort((a, b) => parseInt(a.code) - parseInt(b.code))
                            .map((lot) => (
                              <div
                                key={lot.handleid}
                                onClick={() => handleItemClick(lot.code)}
                                className={`flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors hover:bg-gray-50 ${
                                  lot.code === selectedCode
                                    ? 'border-yellow-400 bg-yellow-50'
                                    : 'border-gray-200'
                                }`}
                              >
                                <div>
                                  <p className="text-s font-bold text-gray-800">Lot {lot.code}</p>
                                  <p className="text-xs text-gray-500">
                                    {lot.landsize}m² • {lot.frontage}m frontage
                                  </p>
                                </div>
                                {editingPriceCode === lot.code ? (
                                  <input
                                    type="number"
                                    autoFocus
                                    value={editingPriceValue}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => setEditingPriceValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        const price = parseFloat(editingPriceValue);
                                        if (!isNaN(price)) updateLotPrice(lot.code, price);
                                        setEditingPriceCode(null);
                                      } else if (e.key === 'Escape') {
                                        setEditingPriceCode(null);
                                      }
                                    }}
                                    onBlur={() => {
                                      const price = parseFloat(editingPriceValue);
                                      if (!isNaN(price)) updateLotPrice(lot.code, price);
                                      setEditingPriceCode(null);
                                    }}
                                    className="w-28 rounded border border-blue-400 px-2 py-0.5 text-right text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  />
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingPriceCode(lot.code);
                                      setEditingPriceValue(String(lot.price));
                                    }}
                                    className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-bold text-gray-800 transition-colors hover:bg-gray-200"
                                  >
                                    ${lot.price.toLocaleString()}
                                  </button>
                                )}
                              </div>
                            ))
                        )}
                      </div>
                    </div>
                  ),
                },
                {
                  label: 'Validation',
                  content: (
                    <div className="space-y-3">
                      {stageValidation.errors.length === 0 &&
                      stageValidation.warnings.length === 0 ? (
                        <p className="text-s text-gray-600">No validation warnings</p>
                      ) : (
                        <>
                          {stageValidation.errors.length > 0 && (
                            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                              <p className="mb-1 text-xs font-bold uppercase text-red-800">
                                Stage errors
                              </p>
                              <ul className="list-disc space-y-1 pl-5 text-xs text-red-700">
                                {stageValidation.errors.map((error) => (
                                  <li key={error}>{error}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {stageValidation.warnings.length > 0 && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                              <p className="mb-1 text-xs font-bold uppercase text-amber-900">
                                Stage warnings
                              </p>
                              <ul className="list-disc space-y-1 pl-5 text-xs text-amber-800">
                                {stageValidation.warnings.map((warning) => (
                                  <li key={warning}>{warning}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        </div>
      </div>
    </div>
  );
};
